import type { JobScheduler, SlicedJob } from '@/contracts';

/**
 * Time-sliced cooperative scheduler for mid-session work (docs/preload-design.md
 * §2.5): tier re-bakes, FX rebuilds, deferred builds. The engine steps it once
 * per frame *after* the render, with a budget of at most ~4 ms, so nothing ever
 * blocks longer than one frame.
 *
 * Steady-state is allocation-free: when both lanes are empty `frame()` returns
 * immediately, and draining uses head indices + slot-nulling rather than
 * `shift()`/`splice()`. The backing arrays are reset (length = 0) whenever a
 * lane fully drains, so they never grow unbounded.
 */

/** Hard cap on `step()` calls per frame, so a job that keeps returning false
 *  without consuming time cannot spin the loop forever. */
const MAX_STEPS_PER_FRAME = 200;

/** Minimum advisory budget handed to a single step. */
const MIN_STEP_BUDGET_MS = 0.5;

export class Jobs implements JobScheduler {
  /** lanes[0] = interactive (preempts), lanes[1] = background. */
  private readonly lanes: [Array<SlicedJob | null>, Array<SlicedJob | null>] = [[], []];
  private readonly heads = [0, 0];
  private count = 0;
  /** Lane the last `peek()` found its job in, so completion pops the right slot
   *  even if the job's own `step()` enqueued higher-priority work. */
  private peekLane = -1;

  get pending(): number { return this.count; }

  enqueue(job: SlicedJob, priority: 'interactive' | 'background' = 'background'): void {
    this.lanes[priority === 'interactive' ? 0 : 1].push(job);
    this.count++;
  }

  cancel(id: string): void {
    for (let l = 0; l < 2; l++) {
      const lane = this.lanes[l];
      for (let i = this.heads[l]; i < lane.length; i++) {
        const j = lane[i];
        if (j !== null && j.id === id) {
          lane[i] = null;
          this.count--;
          try { j.cancel?.(); } catch (err) { console.error(`[jobs] cancel of "${id}" threw`, err); }
        }
      }
    }
  }

  /**
   * Engine-only: run queued slices until `budgetMs` is spent. Called once per
   * frame after the render; not part of the JobScheduler contract surface.
   */
  frame(budgetMs: number): void {
    if (this.count === 0) return;
    const deadline = performance.now() + budgetMs;
    let guard = MAX_STEPS_PER_FRAME;
    do {
      const job = this.peek();
      if (job === null) return;
      let finished = true;
      try {
        finished = job.step(Math.max(MIN_STEP_BUDGET_MS, deadline - performance.now()));
      } catch (err) {
        console.error(`[jobs] "${job.id}" threw — dropped`, err);
      }
      if (finished) this.pop();
    } while (--guard > 0 && this.count > 0 && performance.now() < deadline);
  }

  /** First live job, interactive lane first. Advances heads past cancelled slots. */
  private peek(): SlicedJob | null {
    for (let l = 0; l < 2; l++) {
      const lane = this.lanes[l];
      let h = this.heads[l];
      while (h < lane.length && lane[h] === null) h++;
      this.heads[l] = h;
      if (h < lane.length) {
        this.peekLane = l;
        return lane[h];
      }
      if (lane.length > 0) { lane.length = 0; this.heads[l] = 0; }
    }
    this.peekLane = -1;
    return null;
  }

  /** Remove the job `peek()` returned. Tolerates the job having cancelled itself. */
  private pop(): void {
    const l = this.peekLane;
    if (l < 0) return;
    const lane = this.lanes[l];
    const h = this.heads[l];
    if (h < lane.length && lane[h] !== null) {
      lane[h] = null;
      this.count--;
    }
    this.heads[l] = h + 1;
    this.peekLane = -1;
  }
}
